import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Modal, Alert, Accordion } from 'react-bootstrap';
import { CheckLg, XLg } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../AuthContext";
import api from '../api';
import { gbToSize } from '../utils/bytesSize';

const PLAN_FEATURES = {
  Free:    { guestWall: false, customBranding: false, albums: false, analytics: false, prioritySupport: false },
  Starter: { guestWall: true,  customBranding: true,  albums: true,  analytics: true,  prioritySupport: false },
  Pro:     { guestWall: true,  customBranding: true,  albums: true,  analytics: true,  prioritySupport: false },
  Agency:  { guestWall: true,  customBranding: true,  albums: true,  analytics: true,  prioritySupport: true  },
};

const commonInclusions = [
  'pricing.aiSearch',
  'pricing.qrCodes',
  'pricing.fastUploads',
  'pricing.watermark',
  'pricing.clientAccess',
];

const qualitativeFeatures = [
  { key: 'guestWall',       i18n: 'pricing.guestWall' },
  { key: 'customBranding',  i18n: 'pricing.customBranding' },
  { key: 'albums',          i18n: 'pricing.albums' },
  { key: 'analytics',       i18n: 'pricing.analytics' },
  { key: 'prioritySupport', i18n: 'pricing.prioritySupport' },
];

const PricingPlans = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authenticated, user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    api.get("plans")
      .then((res) => {
        if (res.data?.data) {
          const normalized = res.data.data.map((p) => ({
            ...p,
            is_popular: p.is_popular === 1,
          }));
          setPlans(normalized);
        }
      })
      .catch((err) => console.error("Error fetching plans:", err));
  }, []);

  const getDisplayPrice = (plan) => {
    if (plan.yearly_price > 0 && billingCycle === 'yearly') {
      return (plan.yearly_price / 12).toFixed(1);
    }
    return plan.monthly_price.toFixed(2);
  };

  const getSavingsPercent = (plan) => {
    if (!plan.monthly_price || plan.monthly_price === 0) return 0;
    return Math.round((1 - (plan.yearly_price / 12) / plan.monthly_price) * 100);
  };

  const handleNavigateToCheckout = (planId) => {
    if (!authenticated) {
      return navigate("/signup", { state: { selectedPlanId: planId } });
    }
    if (user?.subscription?.plan_id && user.subscription.plan_id > planId) {
      setSelectedPlanId(planId);
      setShowDowngradeModal(true);
      return;
    }
    navigate("/checkout", { state: { selectedPlanId: planId } });
  };

  const confirmDowngrade = async () => {
    setShowDowngradeModal(false);
    navigate("/checkout", { state: { selectedPlanId, isDowngrade: true } });
  };

  const isCurrent = (plan) => user?.subscription?.plan_id === plan.plan_id;
  const isDowngradePlan = (plan) => user?.subscription?.plan_id > plan.plan_id;

  const getPlanButtonStyle = (plan) => {
    if (isCurrent(plan)) return { background: "#e5e7eb", color: "#9ca3af", border: "none", cursor: "not-allowed" };
    if (isDowngradePlan(plan)) return { background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", cursor: "pointer" };
    if (plan.is_popular) return { background: "#6366f1", color: "#fff", border: "none", cursor: "pointer" };
    return { background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", cursor: "pointer" };
  };

  const getPlanButtonLabel = (plan) => {
    if (isCurrent(plan)) return t('pricing.currentPlan');
    if (isDowngradePlan(plan)) return t('pricing.downgrade');
    if (plan.plan_name === 'Free') return t('pricing.startFree');
    return t('pricing.getStarted');
  };

  const faqItems = [
    { q: t('pricing.faq1Q'), a: t('pricing.faq1A') },
    { q: t('pricing.faq2Q'), a: t('pricing.faq2A') },
    { q: t('pricing.faq3Q'), a: t('pricing.faq3A') },
    { q: t('pricing.faq4Q'), a: t('pricing.faq4A') },
    { q: t('pricing.faq5Q'), a: t('pricing.faq5A') },
  ];

  return (
    <Container className="my-5" style={{ maxWidth: 1100 }}>

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible>
          {successMessage}
        </Alert>
      )}

      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <div style={{
          display: "inline-block",
          background: "#eef2ff",
          color: "#6366f1",
          fontSize: "11px",
          fontWeight: 600,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          padding: "4px 14px",
          borderRadius: "20px",
          marginBottom: "20px",
        }}>
          {t('pricing.pricingBadge')}
        </div>
        <h1 style={{ fontSize: "36px", fontWeight: 800, color: "#111827", lineHeight: 1.15, marginBottom: "14px", letterSpacing: "-0.02em" }}>
          {t('pricing.headline')}
        </h1>
        <p style={{ fontSize: "16px", color: "#6b7280", maxWidth: 560, margin: "0 auto 32px" }}>
          {t('pricing.tagline')}
        </p>

        {/* Billing toggle */}
        <div style={{ display: "inline-flex", alignItems: "center", background: "#f3f4f6", borderRadius: "12px", padding: "4px", gap: "4px" }}>
          {['monthly', 'yearly'].map((cycle) => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              style={{
                padding: "7px 20px",
                borderRadius: "9px",
                border: "none",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s",
                background: billingCycle === cycle ? "#fff" : "transparent",
                color: billingCycle === cycle ? "#111827" : "#9ca3af",
                boxShadow: billingCycle === cycle ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {t(`pricing.${cycle}`)}
              {cycle === 'yearly' && plans.length > 0 && plans.find(p => p.yearly_price > 0) && (() => {
                const savePct = getSavingsPercent(plans.find(p => p.yearly_price > 0 && p.monthly_price > 0));
                return savePct > 0 ? (
                  <span style={{
                    marginLeft: "6px",
                    background: "#dcfce7",
                    color: "#16a34a",
                    fontSize: "10px",
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: "6px",
                  }}>
                    {t('pricing.savePercent', { percent: savePct })}
                  </span>
                ) : null;
              })()}
            </button>
          ))}
        </div>
      </div>

      {/* PLAN CARDS */}
      <Row xs={1} md={2} lg={4} className="g-4">
        {plans.map((plan) => {
          const features = PLAN_FEATURES[plan.plan_name] || {};
          const savings = getSavingsPercent(plan);
          const current = isCurrent(plan);

          return (
            <Col key={plan.plan_id}>
              <div style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                borderRadius: "18px",
                border: plan.is_popular ? "2px solid #6366f1" : "1px solid #e5e7eb",
                boxShadow: plan.is_popular
                  ? "0 8px 32px rgba(99,102,241,0.14)"
                  : "0 1px 4px rgba(0,0,0,0.05)",
                padding: "28px 22px",
                position: "relative",
                transition: "all 0.15s",
              }}>

                {plan.is_popular && (
                  <div style={{
                    position: "absolute",
                    top: "-13px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#6366f1",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "4px 16px",
                    borderRadius: "20px",
                    whiteSpace: "nowrap",
                    letterSpacing: "0.04em",
                  }}>
                    {t('pricing.mostPopular')}
                  </div>
                )}

                {/* Plan name */}
                <div style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: plan.is_popular ? "#6366f1" : "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  marginBottom: "14px",
                  marginTop: plan.is_popular ? "8px" : "0",
                }}>
                  {t(`plan.${plan.plan_name.toLowerCase()}`)}
                </div>

                {/* Price */}
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "6px", visibility: plan.plan_name === 'Free' ? "hidden" : "visible" }}>
                    <span style={{ fontSize: "38px", fontWeight: 800, color: "#111827", lineHeight: 1, letterSpacing: "-0.02em" }}>
                      ${getDisplayPrice(plan)}
                    </span>
                    <span style={{ fontSize: "14px", color: "#9ca3af" }}>/ {t('pricing.perMonth')}</span>
                  </div>

                  {billingCycle === 'yearly' && plan.yearly_price > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{
                        display: "inline-block",
                        background: "#f0fdf4",
                        color: "#16a34a",
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: "6px",
                      }}>
                        {t('pricing.billedYearly')}
                      </span>
                      {savings > 0 && (
                        <span style={{ fontSize: "11px", color: "#16a34a", fontWeight: 600 }}>
                          {t('pricing.savePercent', { percent: savings })}
                        </span>
                      )}
                    </div>
                  )}

                  {billingCycle === 'yearly' && plan.yearly_price > 0 && (
                    <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "5px" }}>
                      {t('pricing.orMonth', { month: plan.monthly_price.toFixed(2) })}
                    </div>
                  )}

                  {plan.yearly_price === 0 && (
                    <div style={{ height: "20px" }} />
                  )}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleNavigateToCheckout(plan.plan_id)}
                  disabled={current}
                  style={{
                    width: "100%",
                    padding: "10px 20px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: 600,
                    transition: "all 0.15s",
                    marginBottom: "8px",
                    ...getPlanButtonStyle(plan),
                  }}
                >
                  {getPlanButtonLabel(plan)}
                </button>

                {/* Trust strip */}
                {!current && (
                  <div style={{ fontSize: "11px", color: "#9ca3af", textAlign: "center", marginBottom: "20px" }}>
                    {plan.plan_name === 'Free' ? t('pricing.noCreditCard') : t('pricing.cancelAnytime')}
                  </div>
                )}
                {current && <div style={{ marginBottom: "20px" }} />}

                {/* Feature divider */}
                <div style={{ borderTop: "1px solid #f3f4f6", marginBottom: "18px" }} />

                {/* Features label */}
                <div style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  marginBottom: "14px",
                }}>
                  {t('pricing.featuresKey')}
                </div>

                {/* Quantitative features */}
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
                  <FeatureRow label={t('pricing.storage')} value={gbToSize(plan.memory_limit_gb)} />
                  <FeatureRow label={t('pricing.maxEvents')} value={plan.max_events === 0 ? t('pricing.noLimit') : plan.max_events} />
                  {plan.expiration_in_day !== null && (
                    <FeatureRow label={t('pricing.expiry')} value={`${plan.expiration_in_day} ${t('pricing.days')}`} />
                  )}

                  {/* File transfers (share.pic-loud.com) */}
                  {plan.transfer_quota_gb != null && (
                    <>
                      <FeatureRow label={t('pricing.transferStorage')} value={gbToSize(plan.transfer_quota_gb)} />
                      <FeatureRow
                        label={t('pricing.transferMax')}
                        value={plan.transfer_max_per_transfer_gb == null ? t('pricing.noLimit') : gbToSize(plan.transfer_max_per_transfer_gb)}
                      />
                      <FeatureRow
                        label={t('pricing.transferExpiry')}
                        value={plan.transfer_max_expiry_days == null
                          ? t('pricing.noLimit')
                          : t('pricing.transferExpiryUpTo', { value: t(`share.expiry.d${plan.transfer_max_expiry_days}`) })}
                      />
                    </>
                  )}

                  {/* Separator */}
                  <div style={{ borderTop: "1px dashed #f3f4f6", margin: "2px 0" }} />

                  {/* Common inclusions */}
                  {commonInclusions.map((incKey) => (
                    <QualitativeRow key={incKey} label={t(incKey)} enabled />
                  ))}

                  {/* Separator */}
                  <div style={{ borderTop: "1px dashed #f3f4f6", margin: "2px 0" }} />

                  {/* Qualitative features */}
                  {qualitativeFeatures.map(({ key, i18n }) => (
                    <QualitativeRow key={key} label={t(i18n)} enabled={!!features[key]} />
                  ))}
                </div>

              </div>
            </Col>
          );
        })}
      </Row>

      {/* FAQ */}
      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "52px", marginTop: "52px" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>
            {t('pricing.faqTitle')}
          </h2>
        </div>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <Accordion flush>
            {faqItems.map((item, idx) => (
              <Accordion.Item key={idx} eventKey={String(idx)} style={{ borderBottom: "1px solid #f3f4f6", borderTop: "none" }}>
                <Accordion.Header style={{ padding: "0" }}>
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#111827" }}>{item.q}</span>
                </Accordion.Header>
                <Accordion.Body style={{ fontSize: "14px", color: "#6b7280", paddingTop: "4px", paddingBottom: "16px" }}>
                  {item.a}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      </div>

      {/* DOWNGRADE MODAL */}
      <Modal show={showDowngradeModal} onHide={() => setShowDowngradeModal(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: "1px solid #f3f4f6" }}>
          <Modal.Title style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>
            {t('pricing.confirmDowngrade')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "14px", color: "#374151", padding: "20px 24px" }}>
          {t('pricing.downgradeWarning')}
          <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "6px" }}>
            {t('pricing.downgradeEffect')}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #f3f4f6", gap: "8px" }}>
          <button
            onClick={() => setShowDowngradeModal(false)}
            style={{
              padding: "8px 18px", borderRadius: "10px",
              border: "1px solid #e5e7eb",
              background: "#f3f4f6", color: "#374151",
              fontSize: "13px", fontWeight: 600, cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t('pricing.cancel')}
          </button>
          <button
            onClick={confirmDowngrade}
            style={{
              padding: "8px 18px", borderRadius: "10px",
              border: "1px solid #fecaca",
              background: "#fef2f2", color: "#ef4444",
              fontSize: "13px", fontWeight: 600, cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {t('pricing.confirmDowngradeBtn')}
          </button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
};

const FeatureRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <div style={{
        width: 18, height: 18, borderRadius: "50%",
        background: "#ecfdf5",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <CheckLg size={10} color="#10b981" />
      </div>
      <span style={{ fontSize: "13px", color: "#374151" }}>{label}</span>
    </div>
    <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{value}</span>
  </div>
);

const QualitativeRow = ({ label, enabled }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <div style={{
      width: 18, height: 18, borderRadius: "50%",
      background: enabled ? "#ecfdf5" : "#f9fafb",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
      border: enabled ? "none" : "1px solid #e5e7eb",
    }}>
      {enabled
        ? <CheckLg size={10} color="#10b981" />
        : <XLg size={8} color="#d1d5db" />
      }
    </div>
    <span style={{ fontSize: "13px", color: enabled ? "#374151" : "#d1d5db" }}>{label}</span>
  </div>
);

export default PricingPlans;
